import { Button, Input } from "@/components/basic";
import { DatePicker } from "@/components/basic/DatePicker/DatePicker";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/primitives/Popover";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form";
import { useI18n } from "@/hooks/useI18n";
import {
  getMaxDateAllowedToReserve,
  getMinDateAllowedToReserve
} from "@/lib/utils";
import { RESERVE_TEXTS, SaveReservationData } from "@/lib/utils-reserve";
import { Office, ResourceType } from "@gettactic/api";
import { CalendarIcon } from "@gettactic/components/src/icons/TacticIcons/CalendarIcon";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import { useAuthenticated, useIsDesignSystem2 } from "@gettactic/hooks";
import { MobileDrawer } from "@v2/mobile/MobileDrawer/MobileDrawer";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useOnclickOutside from "react-cool-onclickoutside";
import { FieldErrors, UseFormReturn } from "react-hook-form";
import { inputIcon } from "../utils/classes";
import { cn } from "@gettactic/helpers";

type ReserveDateMultipleProps = {
  form: UseFormReturn<any>;
  weekDates: Date[];
  officeTimeZone: string;
  office?: Office;
  resType: ResourceType;
  errors: FieldErrors<SaveReservationData>;
  modalRef: React.RefObject<HTMLDivElement>;
};

export default function ReserveFieldDateMultiple({
  form,
  weekDates,
  officeTimeZone,
  office,
  resType,
  errors,
  modalRef
}: ReserveDateMultipleProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const { isBreakpoint } = useBreakpoint();

  const isMd = isBreakpoint("md");

  const { templates } = useI18n(authenticatedUser);
  const [open, setOpen] = useState(false);
  const [localWeekDates, setLocalWeekDates] = useState(weekDates);
  const close = useCallback(() => {
    if (open) {
      // Form is already updated in onChange, just close the picker
    }
    setOpen(false);
  }, [open]);

  const handleClickOutside = (event: Event) => {
    if (modalRef.current) {
      const mouseEvent = event as MouseEvent;
      const boundingRect = modalRef.current.getBoundingClientRect();
      // this is a workaround to NOT close the date picker when clicking on the modal scrollbar
      const isNotOnScrollBar = mouseEvent.clientX < boundingRect.right - 15;

      if (isNotOnScrollBar) {
        close();
      }
    }
  };

  const clickOutsideRef = useOnclickOutside(handleClickOutside);
  const minAllowedReserveDate = useMemo(
    () => getMinDateAllowedToReserve(officeTimeZone, weekDates),
    [officeTimeZone, weekDates]
  );
  const maxAllowedReserveDate = useMemo(
    () => getMaxDateAllowedToReserve(officeTimeZone, office, resType),
    [office, officeTimeZone, resType]
  );

  useEffect(() => {
    setLocalWeekDates(weekDates);
  }, [weekDates]);

  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const value =
    localWeekDates.length > 1
      ? `Multiple Dates (${localWeekDates.length} days)`
      : localWeekDates.length === 0
        ? "Select dates"
        : format(localWeekDates[0], templates.displayDate);

  const isV2 = useIsDesignSystem2();
  const calendarTrigger = (
    <div className="relative flex h-full w-full flex-1 items-center">
      <Input
        variant={isV2 ? "v2" : "default"}
        readOnly={true}
        className="input [.tactic-v2_&]:focus-ring-rainbow h-full flex-1 rounded-md pr-10"
        aria-readonly={true}
        onClick={() => setOpen(true)}
        value={value}
      />
      <div className="absolute right-2.5 flex items-center">
        <CalendarIcon className={cn(inputIcon)} />
      </div>
    </div>
  );

  const calendarContent = (
    <>
      <DatePicker
        variant={isV2 ? "v2" : "default"}
        inline
        minDate={minAllowedReserveDate}
        maxDate={maxAllowedReserveDate ?? undefined}
        selected={null}
        value={""}
        dateFormat={templates.displayDate}
        highlightDates={localWeekDates}
        shouldCloseOnSelect={false}
        onChange={(changeDate) => {
          if (changeDate instanceof Date) {
            const w: Date[] = [...localWeekDates];
            const idx = w.findIndex(
              (x) =>
                format(x, "yyyy-MM-dd") === format(changeDate, "yyyy-MM-dd")
            );
            if (idx === -1) {
              const newWeekDates = [...w, changeDate];
              setLocalWeekDates(newWeekDates);
              // Update form immediately to trigger resource list refresh
              form.setValue("weekDates", newWeekDates, {
                shouldValidate: true
              });
            } else {
              w.splice(idx, 1);
              setLocalWeekDates(w);
              // Update form immediately to trigger resource list refresh
              form.setValue("weekDates", w, {
                shouldValidate: true
              });
            }
          }
        }}
        className="input h-full w-full rounded-md"
        calendarClassName="h-full tailwind-datepicker shadow-none"
        placeholderText={RESERVE_TEXTS.selectStart}
      />
      <div className="mx-auto mt-2 max-w-xs">
        <Button
          className="w-full"
          layout="rounded"
          variant="primary"
          font="boldPrimary"
          onClick={() => {
            setOpen(false);
            // Form is already updated in onChange, just close the picker
          }}
        >
          Continue
        </Button>
        {localWeekDates.length <= 1 ? (
          <p className="mt-2 text-trueGray-300">
            Select multiple dates to book desks in bulk
          </p>
        ) : (
          <button
            className="mt-2 text-orange-300 text-sm"
            onClick={(ev) => {
              ev.preventDefault();
              form.setValue("weekDates", []);
            }}
          >
            Clear {localWeekDates.length}
            {localWeekDates.length === 1 ? " date" : " dates"}
          </button>
        )}
      </div>
    </>
  );

  return (
    <>
      <FormField
        control={form.control}
        name="weekDates"
        render={
          isMd
            ? () =>
                (browserHeight || 0) >= 700 ? (
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>{calendarTrigger}</PopoverTrigger>
                    <PopoverContent
                      className={"flex w-[360px] flex-col items-center"}
                    >
                      {calendarContent}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <>
                    {calendarTrigger}
                    <Dialog open={open} onOpenChange={setOpen}>
                      <DialogContent
                        className={
                          "flex max-h-screen flex-col items-center overflow-auto"
                        }
                      >
                        {calendarContent}
                      </DialogContent>
                    </Dialog>
                  </>
                )
            : () => (
                <>
                  {calendarTrigger}
                  <MobileDrawer isOpen={open} onOpenChange={setOpen}>
                    <div className={"mb-4 flex w-full flex-col items-center"}>
                      {calendarContent}
                    </div>
                  </MobileDrawer>
                </>
              )
        }
      />
      {errors.weekDates ? (
        <p className="text-primary">Please choose a valid date</p>
      ) : null}
    </>
  );
}
